import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSecondaryNavTitle } from "@client/components/secondary-nav";
import { useNavPermissions } from "@client/hooks/use-nav-permissions";
import { Tabs, TabsList, TabsTrigger } from "@tubo/ui/components/ui/tabs";
import { TagUsagePanel } from "@client/components/semantic-platform/analysis/tag-usage-panel";
import { ImpactPanel } from "@client/components/semantic-platform/analysis/impact-panel";
import { QualityPanel } from "@client/components/semantic-platform/analysis/quality-panel";

type AnalysisTab = "usage" | "impact" | "quality";

export const Route = createFileRoute("/_app/semantic-platform/data-governance/analysis/")({
  // tab 入 URL（可分享/可回退）；ns 为领域上下文（impact/quality 跟随）
  validateSearch: (search: Record<string, unknown>): { tab?: AnalysisTab; ns?: string } => {
    return {
      ...(search.tab === "usage" || search.tab === "impact" || search.tab === "quality" ? { tab: search.tab } : {}),
      ...(typeof search.ns === "string" && search.ns ? { ns: search.ns } : {}),
    };
  },
  component: AnalysisCenterPage,
});

/** 可用 Tab 及其静态权限（沿用原三页 nav 门槛：使用分析=分类，影响/质量=影响分析权限） */
const TAB_DEFS: Array<{ value: AnalysisTab; label: string; permission: string }> = [
  { value: "usage", label: "使用分析", permission: "data-governance-classification" },
  { value: "impact", label: "影响分析", permission: "data-governance-impact" },
  { value: "quality", label: "质量中心", permission: "data-governance-impact" },
];

/**
 * 分析中心（使用分析 + 影响分析 + 质量中心三合一）：
 * header 子 Tab 切换三个治理分析视图；Tab 按静态权限过滤，无任何可用权限时显示空态。
 */
function AnalysisCenterPage() {
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const permSet = useNavPermissions();

  const availableTabs = useMemo(
    () => TAB_DEFS.filter((item) => permSet.has(item.permission)),
    [permSet],
  );
  const activeTab = availableTabs.some((item) => item.value === tab)
    ? tab
    : availableTabs[0]?.value;

  useSecondaryNavTitle(
    useMemo(
      () =>
        availableTabs.length > 0 ? (
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold">分析中心</span>
            <Tabs
              value={activeTab}
              onValueChange={(v) => {
                if (v === "usage" || v === "impact" || v === "quality") {
                  void navigate({ to: "/semantic-platform/data-governance/analysis", search: (prev) => ({ ...prev, tab: v }) });
                }
              }}
            >
              <TabsList>
                {availableTabs.map((item) => (
                  <TabsTrigger key={item.value} value={item.value}>{item.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        ) : (
          <span className="text-sm font-semibold">分析中心</span>
        ),
      [availableTabs, activeTab, navigate],
    ),
  );

  if (!activeTab) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <p className="text-sm font-medium">无可用分析模块</p>
          <p className="mt-1 text-xs text-muted-foreground/70">需要「数据治理」相关权限，请联系管理员开通</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 min-w-0" data-tab={activeTab}>
      {activeTab === "usage" && <TagUsagePanel />}
      {activeTab === "impact" && <ImpactPanel />}
      {activeTab === "quality" && <QualityPanel />}
    </div>
  );
}
